import React, { useState, useEffect } from 'react';
import AddMaterialTypeModal from './AddMaterialTypeModal';
import './MaterialTypesListModal.css';

const MaterialTypesListModal = ({ open, onClose, supplier, onMaterialTypesChange }) => {
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMaterialType, setEditingMaterialType] = useState(null);

  // Fetch material types when modal opens
  useEffect(() => {
    if (open && supplier) {
      fetchMaterialTypes();
    }
  }, [open, supplier]);

  const fetchMaterialTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/suppliers/${supplier.id}/material-types`);
      if (!response.ok) {
        throw new Error(`Failed to fetch material types: ${response.statusText}`);
      }
      const data = await response.json();
      setMaterialTypes(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching material types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialTypeAdded = () => {
    fetchMaterialTypes(); // Refresh the list
    setShowAddModal(false);
    setEditingMaterialType(null);
    if (onMaterialTypesChange) {
      onMaterialTypesChange(); // Notify parent to refresh supplier list
    }
  };

  const handleEditMaterialType = (materialType) => {
    setEditingMaterialType(materialType);
    setShowAddModal(true);
  };

  const handleDeleteMaterialType = async (materialType) => {
    if (!window.confirm(`Are you sure you want to delete "${materialType.productCode}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-types/${materialType.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete material type');
      }

      fetchMaterialTypes(); // Refresh the list
      if (onMaterialTypesChange) {
        onMaterialTypesChange(); // Notify parent to refresh supplier list
      }
    } catch (err) {
      setError(err.message);
      console.error('Error deleting material type:', err);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content material-types-list-modal">
          <div className="modal-header">
            <h2>📋 Material Types - {supplier?.name}</h2>
            <button className="modal-close" onClick={onClose}>
              &times;
            </button>
          </div>

          <div className="modal-body">
            {/* Action Bar */}
            <div className="action-bar">
              <div className="material-types-count">
                {materialTypes.length} material type{materialTypes.length !== 1 ? 's' : ''} found
              </div>
              <button 
                className="btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                ➕ Add Material Type
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading material types...</p>
              </div>
            ) : (
              /* Material Types Table */
              <div className="material-types-table-container">
                {materialTypes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No Material Types Found</h3>
                    <p>Add material types that {supplier?.name} supplies.</p>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowAddModal(true)}
                    >
                      ➕ Add First Material Type
                    </button>
                  </div>
                ) : (
                  <table className="material-types-table">
                    <thead>
                      <tr>
                        <th>Product Code</th>
                        <th>Topsheet</th>
                        <th>Adhesive</th>
                        <th>Backing</th>
                        <th>FSC Type</th>
                        <th>Width (mm)</th>
                        <th>Price/m²</th>
                        <th>Rolls</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialTypes.map(materialType => (
                        <tr key={materialType.id} className="material-type-row">
                          <td className="product-code">{materialType.productCode}</td>
                          <td>{materialType.topsheet}</td>
                          <td>{materialType.adhesive}</td>
                          <td>{materialType.backing}</td>
                          <td>{materialType.fscType || '-'}</td>
                          <td>{materialType.jumboWidth ? `${materialType.jumboWidth}mm` : '-'}</td>
                          <td>{materialType.pricePerSqm ? `R${parseFloat(materialType.pricePerSqm).toFixed(2)}` : '-'}</td>
                          <td>
                            <span className="roll-count">
                              {materialType._count?.rawMaterials || 0}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button
                                className="btn-edit-small"
                                onClick={() => handleEditMaterialType(materialType)}
                                title="Edit material type"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-delete-small"
                                onClick={() => handleDeleteMaterialType(materialType)}
                                title="Delete material type"
                                disabled={(materialType._count?.rawMaterials || 0) > 0}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Material Type Modal */}
      <AddMaterialTypeModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingMaterialType(null);
        }}
        onMaterialTypeAdded={handleMaterialTypeAdded}
        editMaterialType={editingMaterialType}
        supplierId={supplier?.id}
        supplierName={supplier?.name}
      />
    </>
  );
};

export default MaterialTypesListModal;
